import {TimelineLite} from "gsap";
import * as HLF from "html-living-framework";
import * as springroll from "springroll";
import {GoogleAnalytics} from "springroll-google-analytics-plugin";
import {Assets, FreePlayAssets, LibraryAssets, PostcardAssets} from "./data/Assets";
import {Environment} from "./data/Environment";
import {DefaultCaptionRenderer} from "./gameobjects/DefaultCaptionRenderer";
import {Analytics} from "./managers/Analytics";
import {AudioChannel} from "./managers/AudioChannel";
import {CaptionManager} from "./managers/CaptionManager";
import {FreePlayScreen} from "./states/FreePlayScreen";
import {GameScreen} from "./states/GameScreen";
import {LoadScreen} from "./states/LoadScreen";
import {SelectLocationScreen} from "./states/SelectLocationScreen";
import {StartScreen} from "./states/StartScreen";

export class Application extends HLF.Application {

    static get instance() {
        if (HLF.Application._instance === undefined) {
            HLF.Application._instance = new Application();
        }
        return HLF.Application._instance as Application;
    }

    public get requiredAssets(): HLF.AssetMapData[] {
        return [];
    }

    public get springroll(): springroll.Application {
        return this._springroll;
    }

    public resizeDisabled = false;

    private _springroll!: springroll.Application;
    private _paused: boolean = false;
    private readonly _bgmVolume = 0.2;
    private _masterVolume: number = 1;

    constructor() {
        super({
            transparent: true,
            preserveDrawingBuffer: (
                PIXI.utils.isMobile.android.device ||
                PIXI.utils.isMobile.amazon.device
            ) ? true : undefined,
        });

        this._audioManager.debug = true;
        this._stateManager.debug = true;
        this._loadManager.debug = true;
        this._popupManager.debug = true;

        PIXI.settings.FILTER_RESOLUTION = this.renderer.resolution;

        // Resize on fullscreen
        document.addEventListener("fullscreenchange", (event) => {
            this.onResize(0.5);
        });

        // Exit fullscreen if TAB is pressed
        window.addEventListener(HLF.Input.Events.KEY_DOWN, (event: KeyboardEvent) => {
            if (HLF.Input.KeyCode.isEventKey(event, HLF.Input.KeyCodes.TAB)) {
                try {
                    document.exitFullscreen().catch(() => {
                        // ignore
                    });
                    window.parent.document.exitFullscreen().catch(() => {
                        // ignore
                    });
                } catch (e) {
                    // ignore
                }
            }
        }, false);
    }

    public createAssetMap() {
        super.createAssetMap();

        HLF.AssetMap.addAssetGroup(HLF.SplashScreen.NAME, Assets.boot);
        HLF.AssetMap.addAssetGroup(SelectLocationScreen.NAME, LibraryAssets);
        HLF.AssetMap.addAssetGroup(FreePlayScreen.NAME, FreePlayAssets);
        HLF.AssetMap.addAssetGroup(GameScreen.NAME, PostcardAssets);
    }

    public createSplashScreen() {
        return super.createSplashScreen();
    }

    public registerStates() {
        super.registerStates();

        this._stateManager.registerState(SelectLocationScreen.NAME, () => new SelectLocationScreen());
        this._stateManager.registerState(GameScreen.NAME, () => new GameScreen());
        this._stateManager.registerState(FreePlayScreen.NAME, () => new FreePlayScreen());
    }

    public registerLoadingScreens() {
        super.registerLoadingScreens();

        this._loadManager.registerLoadScreen(StartScreen.NAME, () => new StartScreen());
        this._loadManager.registerLoadScreen(LoadScreen.NAME, () => new LoadScreen());
    }

    public registerPopups() {
        super.registerPopups();
    }

    public update() {
        super.update();
    }

    public init() {
        super.init();

        this.initSpringroll();

        this.resizer.sizeMax = new PIXI.Point(1664, 768);

        this.initExitGameHandler();

        this._webEventsManager.registerVisibilityChangedCallback((pVisible) => {
            this.onPause(!pVisible);
        });
    }

    protected onResize(pDelay?: number) {
        if (!this.resizeDisabled) {
            super.onResize(pDelay);
        }
        new TimelineLite().call(() => {
            CaptionManager.instance.onResize(this.resizer.getSize());
        }, undefined, this, pDelay);
    }

    protected onLoadRequiredAssetsComplete() {
        super.onLoadRequiredAssetsComplete();

        const captionRenderer = this.stage.addChild(new DefaultCaptionRenderer());
        CaptionManager.instance.setRenderer(captionRenderer);
        CaptionManager.instance.onResize(this._size);

        PubSub.publishSync(
            HLF.STATE_LOAD_STATE,
            new HLF.StateToken({id: SelectLocationScreen.NAME, data: {fromStartScreen: true}},
                StartScreen.NAME,
                HLF.TransitionStep.ShowLoadScreen,
                ...HLF.StateToken.TRANSITION_FIRST_VIEW),
        );
        PubSub.publish(HLF.PUSH_KEYBOARD_LAYER, undefined);

        AudioChannel.BGM.volume = this._bgmVolume;
    }

    private initSpringroll(): void {
        this._springroll = new springroll.Application({
            features: {
                captions: true,
                sound: true,
                vo: true,
                music: true,
                sfx: true,
            },
        });
        springroll.Application.uses(new GoogleAnalytics());

        this._springroll.state.ready.subscribe(() => {
            this.onSpringrollInitialized();
        });

        this._springroll.state.pause.subscribe((isPaused: boolean) => {
            this.onPause(isPaused);
        });

        this._springroll.state.playOptions.subscribe((playOptions: object) => {
            console.log("New playOptions value set to", playOptions);
        });

        this._springroll.state.captionsMuted.subscribe((result: boolean) => {
            this.onCaptionsMuted(result);
        });

        this._springroll.state.soundVolume.subscribe((result: number) => {
            this.onSoundVolume(result);
        });

        this._springroll.state.musicVolume.subscribe((result: number) => {
            this.onMusicVolume(result);
        });

        this._springroll.state.voVolume.subscribe((result: number) => {
            this.onVOVolume(result);
        });

        this._springroll.state.sfxVolume.subscribe((result: number) => {
            this.onSFXVolume(result);
        });
    }

    private onSpringrollInitialized(): void {
        // override
    }

    // Springroll message listeners
    private onCaptionsMuted(pMuted: boolean): void {
        if (Environment.isLive()) {
            CaptionManager.instance.active = !pMuted;
        }
    }

    private onSoundVolume(pResults: number): void {
        this._masterVolume = pResults;
        this.audio.masterVolume = this._masterVolume;
    }

    private onVOVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.VO, pResults);
    }

    private onMusicVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.BGM, pResults * this._bgmVolume);
    }

    private onSFXVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.SFX, pResults);
    }

    private onPause(pIsPaused: boolean): void {
        const wasPaused = this._paused;
        this._paused = pIsPaused;

        if (pIsPaused && !wasPaused) {
            PubSub.publishSync(HLF.GAME_PAUSED, undefined);
            this.audio.masterVolume = 0;
        } else if (wasPaused && !pIsPaused) {
            this.audio.masterVolume = this._masterVolume;
            PubSub.publishSync(HLF.GAME_UNPAUSED, undefined);
        }
    }

    private initExitGameHandler() {
        window.addEventListener("beforeunload", (event) => {
            Analytics.event("endGame");
        });
    }
}
